import { Box, Button, TextField, IconButton, Typography, useTheme } from "@mui/material";
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useContext } from 'react';
import { tokens, useHideMode } from "../../theme";
import useAuth from "../../hooks/useAuth";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import axios from '../../api/axios'
import useAxiosPrivate from "../../hooks/useAxiosPrivate"
import RotateRightIcon from '@mui/icons-material/RotateRight';
import GppGoodIcon from '@mui/icons-material/GppGood';
import { SettingsBarContext } from "../../context/SettingsBarContext";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import handleError from "../../hooks/handleError";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faInfoCircle } from "@fortawesome/free-solid-svg-icons";


const ChangeAccountSettings = () => {
    useEffect(() => {
        document.title = 'Change Password | Fretless Finance';
    }, []);

    const isNonMobile = useMediaQuery("(min-width:600px)");
    const { isSettingsBarOpen, toggleSettingsBar } = useContext(SettingsBarContext);
    // toggleSettingsBar();
    const settingsBarState = "open"
    const effectRan = useRef(false);

    useEffect(() => {
        if (isSettingsBarOpen) {
            toggleSettingsBar();
        }
        return () => {
            effectRan.current = true;
        }
    }, [])



    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const hideTheme = useHideMode();
    const { setAuth } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();


    // Change Password
    // --------------------------------------------------------------------------------------------

    const userRef = useRef();
    const errRef = useRef();


    const [username, setUsername] = useState('');
    const [usernameLabel, setUsernameLabel] = useState('Username');
    const [userFocus, setUserFocus] = useState(false);

    const [old_password, setOldPassword] = useState('');
    const [oldPasswordLabel, setOldPasswordLabel] = useState('Current Password');
    const [new_password, setNewPassword] = useState('');
    const [newPasswordLabel, setNewPasswordLabel] = useState('New Password');
    const [new_password_again, setNewPasswordAgain] = useState('');
    const [newPasswordAgainLabel, setNewPasswordAgainLabel] = useState('New Password (Again)');

    const [validMatch, setValidMatch] = useState(false);
    const [matchFocus, setMatchFocus] = useState(false);

    const [errMsg, setErrMsg] = useState('');
    const [success, setSuccess] = useState(false);

    var [ChgPwdLoading, setChgPwdLoading] = useState(false);



    const USER_REGEX = /^[A-z][A-z0-9]{3,23}$/;
    const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{15,49}$/

    useEffect(() => {
        userRef.current.focus();
    }, [])

    useEffect(() => {
        if (!username) { setUsernameLabel("Username") }
        if (!old_password) { setOldPasswordLabel("Current Password") }
        if (!new_password) { setNewPasswordLabel("New Password") }
        if (!new_password_again) { setNewPasswordAgainLabel("New Password (Again)") }
    }, [username, old_password, new_password, new_password_again])

    useEffect(() => {
        setNewPassword(PWD_REGEX.test(new_password));
        setNewPasswordAgain(new_password === new_password_again);
    }, [new_password, new_password_again])




    const handleChgPwdFormSubmit = async (values) => {
        setErrMsg();
        const validated0 = USER_REGEX.test(values.username)
        const validated1 = PWD_REGEX.test(values.new_password);
        const validated2 = PWD_REGEX.test(values.new_password_again);
        if (!validated0 || !validated1 || !validated2) {
            setErrMsg("Invalid Entry");
            return;
        }
        setSuccess(false);
        setChgPwdLoading(true);
        try {
            const response = await axiosPrivate.post(`/Settings/change-password`,
                JSON.stringify({
                    username: values.username,
                    old_password: values.old_password,
                    new_password: values.new_password
                }),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    timeout: 20000 // 20s timeout
                }
            );
            setChgPwdLoading(false);
            if (response?.data?.detail == "OK") {
                setSuccess(true);
                setUsername('');
                setOldPassword('');
                setNewPassword('');
                setNewPasswordAgain('');
            }
        } catch (err) {
            setChgPwdLoading(false);
            if ((err.response?.status === 401) || (err.response?.status === 403)) {
                navigate('/sign-in', { state: { from: location }, replace: true });
            }
            else {
                handleError(err);
            }
        }
    };
    const handleChgPwdBlur = (event) => {
        if (event.target.name === "username") { setUsernameLabel("") }
        if (event.target.name === "old_password") { setUsernameLabel("") }
        if (event.target.name === "new_password") { setUsernameLabel("") }
        if (event.target.name === "new_password_again") { setUsernameLabel("") }

    };

    const handleChgPwdChange = (event) => {
        console.log(event.target)
        if (event.target.name === "username") { setUsernameLabel("") }
        if (event.target.name === "old_password") { setUsernameLabel("") }
        if (event.target.name === "new_password") { setUsernameLabel("") }
        if (event.target.name === "new_password_again") { setUsernameLabel("") }
    }

    const checkoutSchema = yup.object().shape({
        username: yup.string().matches(USER_REGEX, "Username Not Valid").required("required"),
        old_password: yup.string().required("required"),
        new_password: yup.string().matches(PWD_REGEX, "Password Not Valid").required("required"),
        new_password_again: yup.string().matches(PWD_REGEX, "Password Not Valid").required("required")
    });

    const initialChgPwdValues = {
        username: username,
        old_password: old_password,
        new_password: new_password,
        new_password_again: new_password_again
    }

    // Change Security Questions
    // --------------------------------------------------------------------------------------------

    const SECANSWERS_REGEX = /^[A-z\s][A-z0-9\s]{3,50}$/;

    const securityQuestions = [
        "What was your childhood nickname?",
        "In what city did you meet your spouse/significant other?",
        "What is the name of your favorite childhood friend?",
        "What street did you live on in third grade?",
        "What is your oldest sibling's birthday month and year? (e.g., January 1900)",
        "What is the middle name of your youngest child?",
        "What is your oldest sibling's middle name?",
        "What school did you attend for sixth grade?",
        "What was your childhood phone number including area code? (e.g., 000-000-0000)",
        "What is your oldest cousin's first and last name?",
        "What was the name of your first stuffed animal?",
        "In what city or town did your mother and father meet?",
        "Where were you when you had your first kiss?",
        "What is the first name of the boy or girl that you first kissed?",
        "What was the last name of your third grade teacher?",
        "In what city does your nearest sibling live?",
        "What is your youngest brother's birthday month and year? (e.g., January 1900)",
        "What is your maternal grandmother's maiden name?",
        "In what city or town was your first job?",
        "What is the name of the place your wedding reception was held?",
        "What is the name of a college you applied to but didn't attend?",
        "Where were you when you first heard about 9/11?"
    ];


    const chgSecQuestionsErrRef = useRef();


    const [ans1, setAns1] = useState('');
    const [validAns1, setValidAns1] = useState(false);
    const [ans1Focus, setAns1Focus] = useState(false);

    const [ans2, setAns2] = useState('');
    const [validAns2, setValidAns2] = useState(false);
    const [ans2Focus, setAns2Focus] = useState(false);

    const [ans3, setAns3] = useState('');
    const [validAns3, setValidAns3] = useState(false);
    const [ans3Focus, setAns3Focus] = useState(false);

    const [secQuestionsErrMsg, setSecQuestionsErrMsg] = useState('');
    const [secQuestionsSuccess, setSecQuestionsSuccess] = useState(false);

    const [securityQuestion1, setSecurityQuestion1] = useState('');
    const [securityQuestion2, setSecurityQuestion2] = useState('');
    const [securityQuestion3, setSecurityQuestion3] = useState('');
    const [selectedQuestions, setSelectedQuestions] = useState([]);

    // function to handle security question selection
    const handleQuestionSelection = (e, setQuestion) => {
        const selectedOption = e.target.value;
        setQuestion(selectedOption);
        setSelectedQuestions([...selectedQuestions, selectedOption]);
    }

    useEffect(() => {
        setValidAns1(SECANSWERS_REGEX.test(ans1));
    }, [ans1])

    useEffect(() => {
        setValidAns2(SECANSWERS_REGEX.test(ans2));
    }, [ans2])

    useEffect(() => {
        setValidAns3(SECANSWERS_REGEX.test(ans3));
    }, [ans3])

    useEffect(() => {
        setErrMsg('');
    }, [ans1, ans2, ans3])

    const handleSecQuestionsSubmit = async (e) => {
        e.preventDefault();
        // if button enabled with JS hack
        const v1 = SECANSWERS_REGEX.test(ans1)
        const v2 = SECANSWERS_REGEX.test(ans2)
        const v3 = SECANSWERS_REGEX.test(ans3)
        if (!v1 || !v2 || !v3) {
            setSecQuestionsErrMsg("Invalid Entry");
            return;
        }
        try {
            const response = await axiosPrivate.post("/Settings/change-security-questions",
                JSON.stringify({
                    security_questions: JSON.stringify({
                        "Question 1": securityQuestion1,
                        "Answer 1": ans1,
                        "Question 2": securityQuestion2,
                        "Answer 2": ans2,
                        "Question 3": securityQuestion3,
                        "Answer 3": ans3
                    })
                }),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            setSecQuestionsSuccess(true);
            //clear state and controlled inputs
            //need value attrib on inputs for this

            setSecurityQuestion1('');
            setAns1('');
            setSecurityQuestion2('');
            setAns2('');
            setSecurityQuestion3('');
            setAns3('');

        } catch (err) {
            if ((err.response?.status === 401) || (err.response?.status === 403)) {
                navigate('/sign-in', { state: { from: location }, replace: true });
            }
            else {
                handleError(err);
            }
            chgSecQuestionsErrRef.current.focus();
        }
    }

    // Change Primary Email Credentials
    // --------------------------------------------------------------------------------------------
    const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const EMAILPWD_REGEX = /^[A-z][A-z0-9]{8,50}$/;

    const chgPrimaryEmailCredsErrRef = useRef();

    const [email, setEmail] = useState('');
    const [validEmail, setValidEmail] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [emailPwd, setEmailPwd] = useState('');
    const [validEmailPwd, setValidEmailPwd] = useState(false);
    const [emailPwdFocus, setEmailPwdFocus] = useState(false);

    const [emailSuccess, setEmailSuccess] = useState(false);

    useEffect(() => {
        setValidEmail(EMAIL_REGEX.test(email));
    }, [email])

    useEffect(() => {
        setValidEmailPwd(EMAILPWD_REGEX.test(emailPwd));
    }, [emailPwd])


    const handlePrimaryEmailSubmit = async (e) => {
        e.preventDefault();
        const v1 = SECANSWERS_REGEX.test(ans1)
        const v2 = SECANSWERS_REGEX.test(ans2)
        const v3 = SECANSWERS_REGEX.test(ans3)

        try {
            const response = await axiosPrivate.post("/Settings/change-primary-email-credentials",
                JSON.stringify({
                    security_questions: JSON.stringify({
                        "Answer 1": ans1,
                        "Answer 2": ans2,
                        "Answer 3": ans3
                    }),
                    primary_email: email,
                    primary_email_password: emailPwd
                }),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            setEmailSuccess(true);

            setEmail('');
            setEmailPwd('');

        } catch (err) {
            handleError(err)
            chgPrimaryEmailCredsErrRef.current.focus();
        }
    }


    // --------------------------------------------------------------------------------------------
    return (
        < Box m="20px" >
            <Header title="CHANGE PASSWORD" subtitle="Don't forget it stoopid" />
            < ToastContainer />

            <Formik
                onSubmit={handleChgPwdFormSubmit}
                initialValues={initialChgPwdValues}
                validationSchema={checkoutSchema}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                }) => (
                    <form onSubmit={handleSubmit}>
                        <Box
                            display="grid"
                            gap="30px"
                            gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                            sx={{
                                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                            }}
                        >
                            <Box sx={{ gridColumn: "span 1" }}></Box>
                            <TextField
                                fullWidth
                                variant="filled"
                                type="text"
                                label={usernameLabel}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.username}
                                name="username"
                                ref={userRef}
                                error={!!touched.username && !!errors.username}
                                helperText={touched.username && errors.username}
                                sx={{ gridColumn: "span 2" }}
                            />
                            <Box sx={{ gridColumn: "span 1" }}></Box>
                            <Box sx={{ gridColumn: "span 1" }}></Box>

                            <TextField
                                fullWidth
                                variant="filled"
                                type="password"
                                label={oldPasswordLabel}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.old_password}
                                name="old_password"
                                error={!!touched.old_password && !!errors.old_password}
                                helperText={touched.old_password && errors.old_password}
                                sx={{ gridColumn: "span 2" }}
                            />
                            <Box sx={{ gridColumn: "span 1" }}></Box>
                            <Box sx={{ gridColumn: "span 1" }}></Box>
                            <TextField
                                fullWidth
                                variant="filled"
                                type="password"
                                label={newPasswordLabel}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.new_password}
                                name="new_password"
                                error={!!touched.new_password && !!errors.new_password}
                                helperText={touched.new_password && errors.new_password}
                                sx={{ gridColumn: "span 2" }}
                            />
                            <Box sx={{ gridColumn: "span 1" }}></Box>
                            <Box sx={{ gridColumn: "span 1" }}></Box>
                            <TextField
                                fullWidth
                                variant="filled"
                                type="password"
                                label={newPasswordAgainLabel}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.new_password_again}
                                name="new_password_again"
                                error={!!touched.new_password_again && !!errors.new_password_again}
                                helperText={touched.new_password_again && errors.new_password_again}
                                sx={{ gridColumn: "span 2" }}

                            />
                            <Box sx={{ gridColumn: "span 1" }}></Box>
                            <Box sx={{ gridColumn: "span 1" }}></Box>
                            <Box display="flex" justifyContent="start"
                                sx={{ gridColumn: "span 1" }}>
                                {success ? (
                                    <>
                                        <Typography
                                            variant="h5"
                                            fontWeight="600"
                                            color={colors.greenAccent[400]}>
                                            Password Changed
                                        </Typography>
                                        <GppGoodIcon style={{ color: colors.greenAccent[400], marginTop: "-4px", marginLeft: "5px" }}></GppGoodIcon></>)
                                    : ""}


                            </Box>
                            <Box display="flex" justifyContent="end" mt="20px"
                                sx={{ gridColumn: "span 1" }}>

                                {(ChgPwdLoading) ? <RotateRightIcon
                                    className="rotate-icon"
                                    style={{ marginLeft: "45%" }} /> :
                                    <Button type="submit" color="secondary" variant="contained">
                                        Save
                                    </Button>}
                            </Box>

                        </Box>


                    </form>
                )}
            </Formik>

            {/* SECURITY QUESTIONS */}
            {/* ------------------------------------------------------------------- */}
            <Header title="SECURITY QUESTIONS" subtitle="" />
            <Box display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 5" },
                }}
            >
                <Box sx={{ span: 2 }}></Box>
                <Box sx={{ span: 2 }}>
                    <form onSubmit={handleSecQuestionsSubmit} >

                        <label style={{ marginTop: '1rem' }} htmlFor="securityQuestion1">
                            Security Question 1:
                            <FontAwesomeIcon icon={faCheck} className="hide" />
                            <FontAwesomeIcon icon={faTimes} className="hide" />
                        </label>
                        <select
                            id="securityQuestion1"
                            name="securityQuestion1"
                            value={securityQuestion1}
                            style={{ height: '50px', fontSize: 20, whiteSpace: 'normal' }}
                            onChange={e => handleQuestionSelection(e, setSecurityQuestion1)}
                        >
                            <option value="" disabled>
                                Security Question 1
                            </option>
                            {securityQuestions.map((question, index) => (
                                <option key={index} value={question}>
                                    {question}
                                </option>
                            ))}
                        </select>

                        <label style={{ marginTop: '1rem' }} htmlFor="ans1">
                            Answer 1:
                            <FontAwesomeIcon icon={faCheck} className={validAns1 ? "valid" : "hide"} />
                            <FontAwesomeIcon icon={faTimes} className={validAns1 || !ans1 ? "hide" : "invalid"} />
                        </label>
                        <input
                            type="text"
                            id="ans1"
                            autoComplete="off"
                            onChange={(e) => setAns1(e.target.value)}
                            value={ans1}
                            required
                            aria-invalid={validAns1 ? "false" : "true"}
                            aria-describedby="ans1note"
                            onFocus={() => setAns1Focus(true)}
                            onBlur={() => setAns1Focus(false)}
                        />
                        <p id="ans1note" className={ans1Focus && ans1 && !validAns1 ? "instructions" : "offscreen"}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                            Letters and numbers (and spaces) only.
                        </p>




                        <label style={{ marginTop: '1rem' }} htmlFor="securityQuestion2">
                            Security Question 2:
                            <FontAwesomeIcon icon={faCheck} className="hide" />
                            <FontAwesomeIcon icon={faTimes} className="hide" />
                        </label>
                        <select
                            id="securityQuestion2"
                            name="securityQuestion2"
                            value={securityQuestion2}
                            style={{ height: '50px', fontSize: 20, whiteSpace: 'normal' }}
                            onChange={e => handleQuestionSelection(e, setSecurityQuestion2)}
                        >
                            <option value="" disabled>
                                Security Question 2
                            </option>
                            {securityQuestions.map((question, index) => (
                                <option key={index} value={question}>
                                    {question}
                                </option>
                            ))}
                        </select>

                        <label style={{ marginTop: '1rem' }} htmlFor="ans2">
                            Answer 2:
                            <FontAwesomeIcon icon={faCheck} className={validAns2 ? "valid" : "hide"} />
                            <FontAwesomeIcon icon={faTimes} className={validAns2 || !ans2 ? "hide" : "invalid"} />
                        </label>
                        <input
                            type="text"
                            id="ans2"
                            autoComplete="off"
                            onChange={(e) => setAns2(e.target.value)}
                            value={ans2}
                            required
                            aria-invalid={validAns2 ? "false" : "true"}
                            aria-describedby="ans2note"
                            onFocus={() => setAns2Focus(true)}
                            onBlur={() => setAns2Focus(false)}
                        />
                        <p id="ans2note" className={ans2Focus && ans2 && !validAns2 ? "instructions" : "offscreen"}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                            Letters and numbers (and spaces) only.
                        </p>




                        <label style={{ marginTop: '1rem' }} htmlFor="securityQuestion3">
                            Security Question 3:
                            <FontAwesomeIcon icon={faCheck} className="hide" />
                            <FontAwesomeIcon icon={faTimes} className="hide" />
                        </label>
                        <select
                            id="securityQuestion3"
                            name="securityQuestion3"
                            value={securityQuestion3}
                            style={{ height: '50px', fontSize: 20, whiteSpace: 'normal' }}
                            onChange={e => handleQuestionSelection(e, setSecurityQuestion3)}
                        >
                            <option value="" disabled>
                                Security Question 3
                            </option>
                            {securityQuestions.map((question, index) => (
                                <option key={index} value={question}>
                                    {question}
                                </option>
                            ))}
                        </select>

                        <label style={{ marginTop: '1rem' }} htmlFor="ans3">
                            Answer 3:
                            <FontAwesomeIcon icon={faCheck} className={validAns3 ? "valid" : "hide"} />
                            <FontAwesomeIcon icon={faTimes} className={validAns3 || !ans3 ? "hide" : "invalid"} />
                        </label>
                        <input
                            type="text"
                            id="ans3"
                            autoComplete="off"
                            onChange={(e) => setAns3(e.target.value)}
                            value={ans3}
                            required
                            aria-invalid={validAns3 ? "false" : "true"}
                            aria-describedby="ans3note"
                            onFocus={() => setAns3Focus(true)}
                            onBlur={() => setAns3Focus(false)}
                        />
                        <p id="ans3note" className={ans3Focus && ans3 && !validAns3 ? "instructions" : "offscreen"}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                            Letters and numbers (and spaces) only.
                        </p>
                        <Button
                            disabled={!validAns1 || !validAns2 || !validAns3 ? true : false}
                            onClick={handleSecQuestionsSubmit}
                            color="secondary"
                            variant="contained" style={{ marginTop: 20, marginBottom: 0 }}>
                            Save Security Questions
                        </Button>
                    </form>
                </Box>
            </Box>

            <div sx={{ mt: "30px" }}></div>

            {/* PRIMARY EMAIL */}
            {/* ------------------------------------------------------------------- */}
            <Header title="PRIMARY EMAIL CREDENTIALS" subtitle="" />
            <Box display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 5" },
                }}
            >
                <Box sx={{ span: 2 }}></Box>
                <Box sx={{ span: 2 }}>
                    <form onSubmit={handlePrimaryEmailSubmit} >

                        <label style={{ marginTop: '1rem' }} htmlFor="email">
                            Primary Email:
                            <FontAwesomeIcon icon={faCheck} className={validEmail ? "valid" : "hide"} />
                            <FontAwesomeIcon icon={faTimes} className={validEmail || !email ? "hide" : "invalid"} />
                        </label>
                        <input
                            type="text"
                            id="email"
                            onChange={(e) => setEmail(e.target.value)}
                            value={email}
                            required
                            aria-invalid={validEmail ? "false" : "true"}
                            aria-describedby="emailnote"
                            onFocus={() => setEmailFocus(true)}
                            onBlur={() => setEmailFocus(false)}
                        />
                        <p id="emailnote" className={emailFocus && !validEmail ? "instructions" : "offscreen"}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                            Please enter your primary email to send emails to and from. You will need to answer the security questions as well.
                        </p>

                        <label style={{ marginTop: '1rem' }} htmlFor="emailPwd">
                            Primary Email 3rd Party Password:
                            <FontAwesomeIcon icon={faCheck} className={validEmailPwd ? "valid" : "hide"} />
                            <FontAwesomeIcon icon={faTimes} className={validEmailPwd || !emailPwd ? "hide" : "invalid"} />
                        </label>
                        <input
                            type="password"
                            id="emailPwd"
                            onChange={(e) => setEmailPwd(e.target.value)}
                            value={emailPwd}
                            required
                            aria-invalid={validEmailPwd ? "false" : "true"}
                            aria-describedby="emailpwdnote"
                            onFocus={() => setEmailPwdFocus(true)}
                            onBlur={() => setEmailPwdFocus(false)}
                        />
                        <p id="emailpwdnote" className={emailPwdFocus && !validEmailPwd ? "instructions" : "offscreen"}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                            This password was given to you from your email provider. You will need to answer the security questions as well.
                        </p>
                        <Button
                            disabled={(!validEmail || !validEmailPwd || !validAns1 || !validAns2 || !validAns3) ? true : false}
                            onClick={handlePrimaryEmailSubmit}
                            color="secondary"
                            variant="contained" style={{ marginTop: 20, marginBottom: 0 }}>
                            Save Email Credentials
                        </Button>
                    </form>
                </Box>
            </Box>
        </Box >
    );
};



export default ChangeAccountSettings;